import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Sparkles, Target, ArrowRight, CheckCircle2, Zap, Play } from "lucide-react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { fetchMyFunnel, trackAuthed } from "../lib/analytics";
import { celebrate } from "../components/CelebrationOverlay";
import { toast } from "sonner";

export default function Activation() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [funnel, setFunnel] = useState(null);
  const [mission, setMission] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [completing, setCompleting] = useState(null);
  const [done, setDone] = useState(false);
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    fetchMyFunnel().then((f) => {
      setFunnel(f);
      if (f?.first_project_id) generate(); // reprise sans doublon
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/funnel/first-mission");
      setMission(data);
    } catch {
      toast.error("Impossible de générer la mission. Réessaie.");
    } finally {
      setGenerating(false);
    }
  };

  const firstTask = mission?.tasks?.[0];

  const completeFirst = async () => {
    if (!firstTask) return;
    setCompleting(firstTask.id);
    try {
      const { data: res } = await api.put(`/tasks/${firstTask.id}`, { status: "Terminée" });
      await api.post("/funnel/activate");
      await refresh();
      toast.success(`Première mission lancée · +${res.xp_awarded || firstTask.xp} XP 🎉`);
      if (res.leveled_up) celebrate("level", `Niveau ${res.level ?? ""}`);
      (res.new_badges || []).forEach(() => celebrate("badge"));
      setDone(true);
      api.get("/funnel/referral").then((r) => setReferral(r.data)).catch(() => {});
    } catch {
      toast.error("Une erreur est survenue. Réessaie.");
    } finally {
      setCompleting(null);
    }
  };

  const priorities = funnel?.priorities || [];

  return (
    <div className="min-h-screen bg-[#0A0A0C] px-4 py-10 flex justify-center" data-testid="activation-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Ta première mission</span>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-2">Passe à l'action</h1>
          <p className="text-zinc-500 mt-2">Transforme ton diagnostic en un plan concret et gagne tes premiers XP.</p>
        </div>

        {priorities.length > 0 && !mission && (
          <div className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5" data-testid="activation-priorities">
            <p className="text-xs uppercase tracking-wider text-amber-500 mb-2">Tes priorités identifiées</p>
            <ul className="space-y-1.5">
              {priorities.slice(0, 3).map((p, k) => (
                <li key={k} className="flex items-start gap-2 text-sm text-zinc-300"><Target className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />{p}</li>
              ))}
            </ul>
          </div>
        )}

        {!mission && (
          <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6 text-center">
            <div className="w-14 h-14 rounded-2xl gold-gradient flex items-center justify-center mx-auto mb-4"><Sparkles className="w-7 h-7 text-black" /></div>
            <p className="text-zinc-300 mb-1 font-medium">Génère ta première mission personnalisée</p>
            <p className="text-sm text-zinc-500 mb-5">Notre assistant crée un projet + des tâches adaptés à ton profil.</p>
            <button onClick={generate} disabled={generating} data-testid="generate-mission-btn"
              className="w-full h-12 rounded-xl gold-gradient text-black font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {generating ? <><Loader2 className="w-5 h-5 animate-spin" /> Génération en cours…</> : <>Générer ma première mission <ArrowRight className="w-4 h-4" /></>}
            </button>
            <button onClick={() => navigate("/dashboard")} data-testid="activation-skip" className="mt-4 text-sm text-zinc-500 hover:text-amber-400">Passer cette étape</button>
          </div>
        )}

        {mission && (
          <div className="space-y-4" data-testid="activation-mission">
            <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6">
              <p className="text-xs uppercase tracking-wider text-amber-500 mb-1">Ton projet</p>
              <h2 className="font-display text-xl font-bold" data-testid="mission-title">{mission.project?.title}</h2>
              {mission.project?.description && <p className="text-sm text-zinc-500 mt-1">{mission.project.description}</p>}
              <p className="text-xs text-zinc-600 mt-2">{mission.tasks?.length || 0} tâches · échéance {mission.project?.target_date}</p>
            </div>

            {firstTask && (
              <div className="bg-[#18181E] border border-amber-500/30 rounded-3xl p-6" data-testid="mission-first-task">
                <p className="text-xs uppercase tracking-wider text-amber-500 mb-2">Commence par ça</p>
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0"><Play className="w-4 h-4 text-amber-400" /></span>
                  <div className="flex-1">
                    <p className="font-medium text-zinc-100">{firstTask.title}</p>
                    {firstTask.description && <p className="text-sm text-zinc-500 mt-0.5">{firstTask.description}</p>}
                    <p className="text-xs text-amber-400 mt-1 inline-flex items-center gap-1"><Zap className="w-3 h-3" />+{firstTask.xp} XP</p>
                  </div>
                </div>
                {!done ? (
                  <button onClick={completeFirst} disabled={completing === firstTask.id} data-testid="complete-first-task-btn"
                    className="mt-4 w-full h-12 rounded-xl gold-gradient text-black font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                    {completing === firstTask.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5" /> Terminer cette action</>}
                  </button>
                ) : (
                  <div className="mt-4 text-center" data-testid="activation-done">
                    <p className="text-emerald-400 font-medium inline-flex items-center gap-2 justify-center"><CheckCircle2 className="w-5 h-5" /> Activé ! Tu es lancé.</p>
                    <button onClick={() => navigate("/dashboard")} data-testid="activation-goto-dashboard"
                      className="mt-4 w-full h-12 rounded-xl gold-gradient text-black font-semibold inline-flex items-center justify-center gap-2">
                      Accéder à mon espace <ArrowRight className="w-4 h-4" />
                    </button>
                    {referral && (
                      <div className="mt-5 pt-5 border-t border-zinc-800 text-left" data-testid="referral-card">
                        <p className="text-sm font-medium text-zinc-200">Parraine un créateur, gagne {referral.xp_per_referral} XP</p>
                        <p className="text-xs text-zinc-500 mb-2">+{referral.xp_per_referral} XP à chaque filleul qui s'active. Filleuls activés : {referral.referrals_activated}</p>
                        <div className="flex gap-2">
                          <input readOnly value={referral.link} data-testid="referral-link"
                            className="flex-1 h-10 px-3 rounded-lg bg-[#0F0F13] border border-zinc-800 text-xs text-zinc-400" />
                          <button data-testid="referral-copy" onClick={() => { navigator.clipboard?.writeText(referral.link); toast.success("Lien copié !"); }}
                            className="h-10 px-4 rounded-lg border border-amber-500/40 text-amber-300 text-sm hover:bg-amber-500/10">Copier</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!done && (
              <button onClick={() => navigate("/dashboard")} data-testid="activation-continue-later" className="w-full text-sm text-zinc-500 hover:text-amber-400 py-2">Continuer plus tard</button>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}
